import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  setMuted,
  setOutputVolume,
  setSubtitlesOn,
} from "@store/avatarSlice";
import {
  Box,
  Drawer,
  Fab,
  IconButton,
  Popover,
  Slider,
  Stack,
  Tooltip,
} from "@mui/material";
import MuteButton from "@components/MuteButton/MuteButton";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import TuneIcon from "@mui/icons-material/Tune";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import SideBar from "@components/SideBar/SideBar";
import { useTranslation } from "react-i18next";
import Timer from "@components/Timer";
import SubtitlesButton from "@components/SubtitlesButton";
import ConversationAudioOptionsDialog from "@components/CompanionToolbar/ConversationAudioOptionsDialog";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";

const CompanionToolbar: FC<{
  vertical?: boolean;
  /** When false, hides the navigation drawer trigger (e.g. on Talk). */
  showMenu?: boolean;
  onStop: () => void;
  stopping: boolean;
  startTime: Date | null;
}> = ({ vertical = true, showMenu = true, onStop, stopping, startTime }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { muted, subtitlesOn, outputVolume, processing, talking } =
    useSelector((state: RootState) => state.avatar);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [audioOptionsOpen, setAudioOptionsOpen] = useState(false);
  const [volumeAnchor, setVolumeAnchor] = useState<HTMLElement | null>(null);
  const {
    voiceInputMode,
    startPttCapture,
    stopPttCapture,
    runPttInterrupt,
    registerPttAutoResumeCanceler,
  } = useCompanionConversation();
  const recording = useSelector((state: RootState) => state.microphone.recording);
  const micNotFound = useSelector((state: RootState) => state.microphone.micNotFound);
  const micPermissionDenied = useSelector(
    (state: RootState) => state.microphone.micPermissionDenied,
  );
  const micBlocked = micNotFound || micPermissionDenied;
  const hadAssistantPlaybackRef = useRef(false);
  const lastManualStopPttRef = useRef(0);

  const handlePttFabClick = useCallback(async () => {
    if (recording) {
      lastManualStopPttRef.current = Date.now();
      hadAssistantPlaybackRef.current = false;
      stopPttCapture();
      return;
    }
    if (processing || talking) {
      await runPttInterrupt();
      startPttCapture();
      hadAssistantPlaybackRef.current = false;
      return;
    }
    startPttCapture();
  }, [
    processing,
    recording,
    runPttInterrupt,
    startPttCapture,
    stopPttCapture,
    talking,
  ]);

  useEffect(() => {
    // Gallery slide interrupts call `interruptPlayback`, flipping `talking` to false
    // mid-playback. Without this canceler the auto-resume effect below would start PTT
    // during the debounced `NewPhoto` window and the mic's PCM stream would clobber the
    // reply's subtitles.
    registerPttAutoResumeCanceler(() => {
      hadAssistantPlaybackRef.current = false;
      lastManualStopPttRef.current = Date.now();
    });
    return () => {
      registerPttAutoResumeCanceler(null);
    };
  }, [registerPttAutoResumeCanceler]);

  useEffect(() => {
    if (voiceInputMode !== "push_to_talk") {
      return;
    }
    if (talking) {
      hadAssistantPlaybackRef.current = true;
    }
    const idle = !processing && !talking;
    if (!idle || recording || micBlocked) {
      return;
    }
    if (!hadAssistantPlaybackRef.current) {
      return;
    }
    if (Date.now() - lastManualStopPttRef.current < 250) {
      return;
    }
    // Defer so MediaRecorder can settle to "inactive" after a prior stop/onstop, and Redux
    // from the avatar pipeline is visible to canStartUserCaptureLive in the mic bridge.
    queueMicrotask(() => {
      startPttCapture();
      hadAssistantPlaybackRef.current = false;
    });
  }, [
    micBlocked,
    processing,
    recording,
    startPttCapture,
    talking,
    voiceInputMode,
  ]);

  const pushToTalkLabel = recording
    ? t("companionConversation.pushToTalk.stop")
    : processing || talking
      ? t("companionConversation.pushToTalk.interruptAndRecord")
      : t("companionConversation.pushToTalk.start");

  const handleMenuClick = () => {
    setDrawerOpen((open) => !open);
  };

  const toggleMute = () => {
    dispatch(setMuted(!muted));
  };

  const toggleSubtitles = () => {
    dispatch(setSubtitlesOn(!subtitlesOn));
  };

  return (
    <>
      <Stack
        justifyContent="space-between"
        flexDirection={vertical ? "column" : "row"}
        width={vertical ? "auto" : "100%"}
        height={vertical ? "100%" : "auto"}
        alignItems="center"
        padding={vertical ? "10px 10px 20px" : "8px 16px"}
        boxSizing="border-box"
        gap="16px"
      >
        {showMenu ? (
          <IconButton
            size="medium"
            aria-label={t("header.openDrawerButton")}
            onClick={handleMenuClick}
            sx={{ color: "#fff" }}
          >
            {!drawerOpen ? (
              <MenuIcon fontSize="medium" />
            ) : (
              <MenuOpenIcon fontSize="medium" />
            )}
          </IconButton>
        ) : null}

        <Stack
          flex="1"
          alignItems="center"
          flexDirection={vertical ? "column" : "row"}
          justifyContent="space-between"
        >
          <Stack gap="16px" flexDirection={vertical ? "column" : "row"}>
            <MuteButton muted={muted} onClick={toggleMute} />

            <SubtitlesButton on={subtitlesOn} onClick={toggleSubtitles} />

            <IconButton
              size="medium"
              aria-label={t("companion.audioOptions.openOptions")}
              onClick={() => setAudioOptionsOpen(true)}
              sx={{ color: "#fff" }}
            >
              <TuneIcon fontSize="medium" />
            </IconButton>

            <IconButton
              size="medium"
              aria-label={t("companion.audioOptions.ariaVolume")}
              onClick={(e) => setVolumeAnchor(e.currentTarget)}
              sx={{ color: "#fff" }}
            >
              <VolumeUpIcon fontSize="medium" />
            </IconButton>
          </Stack>

          <Stack
            gap="16px"
            alignItems="center"
            flexDirection={vertical ? "column" : "row"}
          >
            <Timer startTime={startTime} variant="light" />
            <Fab
              id="stopcall-button"
              onClick={onStop}
              color="error"
              size="small"
              disabled={stopping}
            >
              <CallEndOutlinedIcon />
            </Fab>
          </Stack>
        </Stack>
      </Stack>

      {voiceInputMode === "push_to_talk" ? (
        <Box
          sx={{
            position: "fixed",
            right: (theme) =>
              `calc(${theme.spacing(2)} + env(safe-area-inset-right, 0px))`,
            bottom: (theme) =>
              `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Tooltip title={pushToTalkLabel}>
            <span>
              <Fab
                color={recording ? "warning" : "primary"}
                size="large"
                aria-label={pushToTalkLabel}
                onClick={() => void handlePttFabClick()}
                disabled={!recording && (micBlocked || muted)}
              >
                {recording ? (
                  <CheckCircleRoundedIcon />
                ) : (
                  <RecordVoiceOverIcon />
                )}
              </Fab>
            </span>
          </Tooltip>
        </Box>
      ) : null}

      <Popover
        open={Boolean(volumeAnchor)}
        anchorEl={volumeAnchor}
        onClose={() => setVolumeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: { sx: { px: 2, py: 1.5, minWidth: 160 } },
        }}
      >
        <Slider
          size="small"
          value={outputVolume ?? 1}
          min={0}
          max={1}
          step={0.01}
          onChange={(_, v) =>
            dispatch(setOutputVolume(Array.isArray(v) ? v[0] : v))
          }
          sx={{ width: 140 }}
        />
      </Popover>

      <ConversationAudioOptionsDialog
        open={audioOptionsOpen}
        onClose={() => setAudioOptionsOpen(false)}
      />

      {showMenu ? (
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <SideBar onNavigate={() => setDrawerOpen(false)} />
        </Drawer>
      ) : null}
    </>
  );
};

export default CompanionToolbar;
